import axios from 'axios'

// 创建axios实例
const request = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
  timeout: 360000
})

// 请求拦截器
request.interceptors.request.use(
  config => {
    // 从localStorage获取token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    return response
  },
  error => {
    if (error.response && error.response.status === 401) {
      // 未登录或token过期
      localStorage.removeItem('token')
      const currentTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`
      window.location.href = `/login?redirect=${encodeURIComponent(currentTarget)}`
    }
    return Promise.reject(error)
  }
)

// 书籍相关接口
export const bookApi = {
  // 获取书籍列表
  getBooks(params) {
    return request.get('/books', { params })
  },

  // 获取当前用户最近阅读的一本书（独立于书籍分页）
  getContinueReading() {
    return request.get('/books/continue-reading')
  },

  getRecentReading() {
    return request.get('/books/recent-reading', { params: { limit: 3 } })
  },

  getRecentlyAdded() {
    return request.get('/books/recently-added', { params: { limit: 8 } })
  },

  // 搜索书籍
  searchBooks(params) {
    return request.get('/books/search', { params })
  },

  // 获取书籍详情
  getBookDetail(bookId) {
    return request.get(`/books/${bookId}`)
  },

  getPdfResourceUrl(bookId) {
    return `${request.defaults.baseURL}/books/${bookId}/resource`
  },

  uploadAudioBook(formData, onUploadProgress) {
    return request.post('/books/upload-audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    })
  },

  getAudioTracks(bookId) {
    return request.get(`/books/${bookId}/tracks`)
  },

  getAudioStreamAccess(bookId, trackId) {
    return request.post(`/books/${bookId}/tracks/${trackId}/stream-access`)
  },

  getAudioStreamUrl(trackId, accessToken) {
    return `${request.defaults.baseURL}/media/audio/${trackId}/stream?access=${encodeURIComponent(accessToken)}`
  },

  reorderAudioTracks(bookId, trackIds) {
    return request.put(`/books/${bookId}/tracks/order`, { trackIds })
  },

  listPdfBookmarks(bookId) {
    return request.get('/bookmarks', { params: { bookId } })
  },

  // 获取书籍章节列表（全量，阅读器随机导航需要）
  getBookChapters(bookId) {
    return request.get(`/books/${bookId}/chapters`)
  },

  // 章节目录分页切片：返回 {total, offset, limit, list}（应对上万章，详情页按需加载）
  getBookChaptersSlice(bookId, offset = 0, limit = 100) {
    return request.get(`/books/${bookId}/chapters/slice`, { params: { offset, limit } })
  },

  // 上传书籍（visibility: 'PUBLIC' | 'PRIVATE'，默认私有；category 可选分类）
  uploadBook(formData, onUploadProgress, visibility, category) {
    const params = {}
    if (visibility) params.visibility = visibility
    if (category) params.category = category
    return request.post('/books/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: Object.keys(params).length ? params : undefined,
      onUploadProgress
    })
  },

  // 从URL导入书籍（visibility: 'PUBLIC' | 'PRIVATE'，默认私有）
  downloadBook(url, onDownloadProgress, visibility) {
    return request.post('/books/download', { url, visibility }, {
      onDownloadProgress
    })
  },

  // 删除书籍
  deleteBook(bookId) {
    return request.delete(`/books/${bookId}`)
  },

  // 编辑书籍信息
  updateBook(bookId, data) {
    return request.put(`/books/${bookId}`, data)
  },

  // 书内全文搜索
  searchInBook(bookId, keyword) {
    return request.get(`/books/${bookId}/search`, { params: { keyword } })
  },

  // 下载整本原文（返回二进制流，需带鉴权头）
  downloadOriginal(bookId) {
    return request.get(`/books/${bookId}/download`, { responseType: 'blob' })
  }
}

// 章节相关接口
export const chapterApi = {
  // 获取章节内容
  getChapterContent(chapterId) {
    return request.get(`/chapters/${chapterId}`)
  }
}

// 阅读进度相关接口
export const progressApi = {
  // 获取阅读进度
  getReadingProgress(bookId) {
    return request.get(`/reading-progress/${bookId}`)
  },

  // 保存阅读进度
  saveReadingProgress(data) {
    return request.post('/reading-progress', data)
  },

  // 获取书籍的阅读进度
  getBookProgress(bookId) {
    return request.get(`/books/${bookId}/progress`)
  }
}

// 用户相关接口
export const userApi = {
  // 用户登录
  login(data) {
    return request.post('/auth/login', data)
  },

  // 用户注册
  register(data) {
    return request.post('/auth/register', data)
  },

  // 获取用户信息
  getUserInfo() {
    return request.get('/auth/current-user')
  },

  // 更新用户信息
  updateUserInfo(data) {
    return request.put('/user/info', data)
  },

  // 修改密码
  changePassword(data) {
    return request.put('/user/password', data)
  }
}

// 书签相关接口
export const bookmarkApi = {
  // 列出某本书的书签
  list(bookId) {
    return request.get('/bookmarks', { params: { bookId } })
  },
  // 当前用户全部书签/划线（跨书聚合，「我的笔记」页用，每条带 bookTitle）
  listAll() {
    return request.get('/bookmarks/all')
  },
  // 添加书签
  add(data) {
    return request.post('/bookmarks', data)
  },
  // 删除书签
  remove(id) {
    return request.delete(`/bookmarks/${id}`)
  }
}

// 收藏/追书接口
export const favoriteApi = {
  // 加入书架
  add(bookId) {
    return request.post('/favorites', { bookId })
  },
  // 移出书架
  remove(bookId) {
    return request.delete(`/favorites/${bookId}`)
  },
  // 当前用户收藏的 bookId 列表
  ids() {
    return request.get('/favorites/ids')
  }
}

// 阅读时长统计接口
export const statsApi = {
  // 心跳：累加阅读秒数
  heartbeat(data) {
    return request.post('/reading-stats/heartbeat', data)
  },
  // 累计阅读时长
  total() {
    return request.get('/reading-stats/total')
  }
}

// 用户设置相关接口
export const settingsApi = {
  // 获取当前用户设置
  getUserSettings() {
    return request.get('/user-settings')
  },
  // 更新用户设置（部分字段）
  updateUserSettings(data) {
    return request.put('/user-settings', data)
  }
}

export default {
  book: bookApi,
  chapter: chapterApi,
  progress: progressApi,
  user: userApi,
  settings: settingsApi,
  bookmark: bookmarkApi,
  favorite: favoriteApi,
  stats: statsApi
}
